<template>
  <node-view-wrapper
    ref="wrapper"
    class="resizable-wrapper"
    as="div"
  >
    <div
      ref="resizableContent"
      :class="{ 'ProseMirror-selectednode': selected || isNodeWithinSelection }"
      class="resizable-content"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <div
        v-show="handlesVisible"
        class="resize-handle-left"
        @click.stop.prevent
        @mousedown.stop.prevent="mouseDownHandler('left')"
      />
      <div
        v-show="handlesVisible"
        class="resize-handle-right"
        @click.stop.prevent
        @mousedown.stop.prevent="mouseDownHandler('right')"
      />
      <div
        v-if="optionsDropdown && mouseover"
        ref="optionButtons"
        class="absolute top-0 right-0 h-auto p-2 z-10 flex"
      >
        <v-popover
          interactive
          tag="div"
          content-tag="div"
          placement="bottom-end"
          class="p-0"
          :trigger="'click'"
          :role="'poper'"
        >
          <template #default>
            <div
              class="image-option-button bg-important-bg border-important-border border border-solid rounded w-6 h-6 my-0 mx-1 flex items-center justify-center cursor-pointer hover:bg-important-hover"
              @mousedown.stop.prevent
            >
              <em
                ref="imageOptionsButton"
                class="bg-icon-default w-5 h-5 block"
                :style="[getCustomIconStyle(iconPath('more-vertical'))]"
              />
            </div>
          </template>
          <template #content="{ hide, state }">
            <ListDropdown
              v-if="state.isVisible"
              class="image-list-dropdown image-list-dropdown p-1"
              :position="'relative'"
              :items="imageOptionItems"
              :width="240"
              :filtered-out-key-codes="['Space', 'Delete']"
              :disabled-key-navigation="false"
              @click="
                (item, _index, _event) =>
                  handleImageOptionsDropdownItemsClick(item, _index, _event, hide)
              "
              @closeEscape="hide"
            />
          </template>
        </v-popover>
        <div
          v-if="editor.view.editable"
          class="image-option-button bg-important-bg border-important-border border border-solid rounded w-6 h-6 my-0 mx-1 flex items-center justify-center hover:bg-important-hover"
          @click.stop="handleDelete"
        >
          <em
            class="cursor-pointer bg-icon-default w-5 h-5 block"
            :style="[getCustomIconStyle(iconPath('trash'))]"
          />
        </div>
      </div>
      <NodeViewContent
        v-if="extension.options.tagName !== $constants.stImage"
        v-bind="{ ...node.attrs, [$constants.blockAttributeName]: null }"
        ref="contentNode"
        data-resize-node="true"
        :as="extension.options.tagName || 'div'"
        :class="{ 'ProseMirror-resizing': isResizing }"
      />
      <StImage
        v-else
        v-bind="{ ...node.attrs, [$constants.blockAttributeName]: null }"
        ref="imageNode"
        data-resize-node="true"
        :imgsrc="node.attrs.imgsrc"
        :class="{ 'ProseMirror-resizing': isResizing }"
        :data-block-id="node.attrs['block-id']"
      />
    </div>
  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps, NodeViewContent } from '@tiptap/vue-3';
import { Youtube } from '@tiptap/extension-youtube';
import constants from '@/utilities/constants';
import ListDropdown from '@/components/widgets/ListDropdown';
import customIconMixin from '@/mixins/customIconsMixin';
import getFileIdFromUrl from '@/utilities/getFileIdFromUrl';
import { downloadFile } from '@/utilities/downloads';
import { checkWidth, calculateHeight } from '@/components/editor/plugins/fileReadUpload';
import useCopyImage from '@/utilities/composables/useCopyImage.ts';
import useIsBrowser from '@/utilities/composables/useIsBrowser.ts';
import StImage from '@/webcomponents/StImage.vue';

export default {
  components: {
    NodeViewWrapper,
    NodeViewContent,
    ListDropdown,
    StImage,
  },

  mixins: [customIconMixin],

  props: nodeViewProps,

  setup() {
    const { copyImage } = useCopyImage();
    const { isChromium } = useIsBrowser();
    return { copyImage, isChromium };
  },

  data() {
    return {
      // When is resizing
      isResizing: false,
      // We keep last movement calculation so we can
      // determine if the image is going to get larger or smaller
      lastMovement: 0,
      // Original aspect ratio of the img
      aspectRatio: 0,

      handlesVisible: false,

      dragStartX: null,

      increasing: false,

      movementDelta: 0,

      throttleTimeout: null,

      mouseover: false,
      dropdownTop: null,
      dropdownRight: null,
      isInTable: false,
      listenersAdded: false,
      withinSelectinTimeout: null,
      isNodeWithinSelection: false,
    };
  },

  computed: {
    imageOptionItems() {
      return [
        {
          id: 'fullscreen',
          label: this.translate('fullscreen'),
          customIconName: this.iconPath('maximize-2'),
          meta: this.editor.view.editable ? 'Space' : null,
          shortcutKeyCode: 'Space',
        },
        {
          id: 'download',
          label: this.translate('download'),
          customIconName: this.iconPath('download'),
        },
        this.isChromium
          ? {
              id: 'copy',
              label: this.translate('copy'),
              customIconName: this.iconPath('copy'),
              separator: this.editor.view.editable,
            }
          : null,
        this.editor.view.editable
          ? {
              id: 'delete',
              label: this.translate('delete'),
              customIconName: this.iconPath('trash'),
              meta: 'Del',
              shortcutKeyCode: 'Delete',
            }
          : null,
      ];
    },

    optionsDropdown() {
      return this.node.type.name === this.$constants.stImage;
    },
  },

  mounted() {
    this.calculateImagesSize();
    this.$nextTick(() => {
      this.checkIsInTable();
      this.checkYoutubeUrl();
      if (this.$refs?.contentNode?.$el) {
        this.$refs.contentNode.$el.dataset.blockId =
          this.node.attrs[this.$constants.blockAttributeName];
      }
    });
    this.aspectRatio = this.node.attrs.width / this.node.attrs.height;
    this.addEventListeners();

    this.editor.on('selectionUpdate', this.isWithinSelection);
  },

  beforeUnmount() {
    this.removeEventListeners();
  },

  activated() {
    this.addEventListeners();
  },

  deactivated() {
    this.removeEventListeners();
  },

  methods: {
    isWithinSelection() {
      clearTimeout(this.withinSelectinTimeout);
      this.withinSelectinTimeout = setTimeout(() => {
        const pos = this.getPos();
        const { $from, $to } = this.editor.view.state.selection;
        this.isNodeWithinSelection = $from.pos <= pos && pos < $to.pos;
      }, 50);
    },
    onMouseEnter() {
      // do not display handles if the editor is not editable
      if (this.editor.view.editable) {
        this.handlesVisible = true;
      }
      this.mouseover = true;
    },
    onMouseLeave(event) {
      if (
        event?.relatedTarget?.matches(
          '.image-list-dropdown, .dropdown-item-column, .item-row, .dropdown-item-label'
        ) ||
        event.relatedTarget?.parentNode?.matches('.list-dropdown-scroll')
      ) {
        return;
      }
      if (this.isResizing) return;
      this.handlesVisible = false;
      this.mouseover = false;
    },
    onMouseLeaveWrap() {
      if (this.isResizing) return;
      this.setResizing(false);
      this.handlesVisible = false;
    },
    setResizing(isResizing) {
      this.isResizing = isResizing;
      this.editor.storage.resizing.isResizing = isResizing;
    },
    mouseDownHandler(handle) {
      if (this.editor.storage.MouseOver.dragHandleBlockId !== '') {
        this.editor.commands.hideDragHandle();
      }
      this.setResizing(true);
      this.dragStartX = event.clientX;
      this.activeHandle = handle;
      document.addEventListener('mouseup', this.onMouseUp);
      document.addEventListener('mousemove', this.onMouseMoveThrottled);
      document.body.classList.add('ProseMirror-resizing');
    },
    onMouseMove(event) {
      if (!this.isResizing) return;

      const isLeftHandle = this.activeHandle === 'left';

      const { clientX } = event;
      const { width, height } = this.node.attrs;
      const { aspectRatio } = this;

      this.increasing = isLeftHandle ? clientX < this.dragStartX : clientX > this.dragStartX;

      const delta = this.increasing ? this.dragStartX - clientX : clientX - this.dragStartX;

      const roundToWhole = (num) => Math.round(num);

      this.movementDelta = Math.abs(delta) * 2;
      const heightDelta = roundToWhole(this.movementDelta / aspectRatio);

      const newWidth = this.increasing ? width + this.movementDelta : width - this.movementDelta;
      const newHeight = roundToWhole(this.increasing ? height + heightDelta : height - heightDelta);

      if (newWidth < constants.stImageMinWidth) return;
      if (this.isInTable && !this.checkParentWidth(newWidth)) {
        return;
      }

      this.updateAttributes({
        width: newWidth,
        height: newHeight,
        style: `width: ${newWidth}px;`,
      });
      this.dragStartX = clientX;
    },
    onMouseMoveThrottled(event) {
      if (!this.throttleTimeout) {
        this.throttleTimeout = setTimeout(() => {
          this.$nextTick(() => {
            this.onMouseMove(event);
          });
          this.throttleTimeout = null;
        }, 10);
      }
    },
    onMouseUp() {
      this.setResizing(false);
      this.dragStartX = null;
      this.activeHandle = null;
      document.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('mousemove', this.onMouseMoveThrottled);
      document.body.classList.remove('ProseMirror-resizing');
    },

    async handleImageOptionsDropdownItemsClick(item, _index, _event, hide) {
      if (typeof hide === 'function') {
        hide();
      }
      switch (item.id) {
        case 'delete':
          this.handleDelete();
          break;
        case 'fullscreen':
          this.showImageInFullScreen();
          break;
        case 'download':
          downloadFile(this.node.attrs.imgsrc, this.node.attrs.alt);
          break;
        case 'copy':
          const imgElement = this.$refs.imageNode?.$refs?.imgNode;

          if (!imgElement) {
            return;
          }

          this.copyImage(imgElement, { nodeSrc: this.node.attrs.imgsrc });
          break;
        default:
          break;
      }
    },

    handleDelete() {
      this.deleteNode();
    },

    showImageInFullScreen() {
      const url = this.node.attrs.imgsrc;
      const id = getFileIdFromUrl(url);

      if (!id) return;
      this.editor.commands.displayFullScreenImage(id);
    },

    checkYoutubeUrl() {
      const { node, editor } = this;
      if (node.type?.name !== 'youtube') return;
      const { src } = node.attrs;
      if (!src) return;
      // check if it's the embed url
      if (src.includes('embed')) return;
      const youtubeExtension = editor.extensionManager?.extensions?.find(
        (extension) => extension.name === 'youtube'
      );

      Youtube.config.renderHTML.apply({ options: youtubeExtension.options }, [
        { HTMLAttributes: node.attrs },
      ]);
    },

    calculateImagesSize() {
      // this is mainly added for images imported from Shortcut
      // images don't have width or height set on tag so we need to read image
      if (this.node.type.name === 'st-image') {
        if (!this.node.attrs.width || !this.node.attrs.height) {
          const img = new Image();
          img.src = this.node.attrs.imgsrc || '';
          img.onload = () => {
            const width = checkWidth(this.editor.id, img.width);
            let height = this.node.attrs.height;
            if (width <= img.width) {
              height = calculateHeight(img.width, img.height, width);
            }

            this.aspectRatio = width / height;
            this.updateAttributes({
              height,
              width,
              style: `width: ${width}px; height: ${height}px;`,
            });
          };
        }
      }
    },

    checkIsInTable() {
      const parent = this.$refs?.wrapper?.$el?.parentNode;
      if (parent?.tagName === 'TD' || parent?.tagName === 'TH') {
        this.isInTable = true;
      }
    },
    checkParentWidth(newWidth) {
      const parent = this.$refs.wrapper.$el.parentNode;
      const parentWidth = parent.getAttribute('colwidth') || parent.offsetWidth;
      // 16 is the padding of the parent( left + right 8px each)
      return newWidth > parentWidth - 16 ? false : true;
    },

    addEventListeners() {
      if (this.listenersAdded) return;
      this.$el.addEventListener('mouseleave', this.onMouseLeaveWrap);
      this.listenersAdded = true;
    },

    removeEventListeners() {
      if (!this.listenersAdded) return;
      this.$el.removeEventListener('mouseleave', this.onMouseLeaveWrap);
      this.listenersAdded = false;
    },
  },
};
</script>

<style lang="scss">
$optionsButtonDim: 24px;

.resizable-wrapper {
  display: flex;
  margin: 8px auto;
  max-width: 100%;
}

.resizable-content {
  max-width: 100%;
  position: relative;
  border-radius: 8px;

  .tooltip-inner {
    &.popover-inner {
      @apply p-0 rounded-lg;
    }
  }

  .resize-handle-left,
  .resize-handle-right {
    $resizeHandleHeight: 48px;
    height: $resizeHandleHeight;
    width: 6px;
    background-color: black;
    border: 1px solid white;
    box-sizing: border-box;
    opacity: 0.7;
    position: absolute;
    border-radius: 8px;
    z-index: 1;
    top: calc(50% - #{calc($resizeHandleHeight / 2)});
    cursor: col-resize;
    margin: 0 4px;
  }

  .resize-handle-right {
    right: 0px;
  }

  .ProseMirror-resizing {
    pointer-events: none;
  }

  .options-wrap {
    @apply absolute top-0 right-0 h-auto p-2 z-10 flex;
    .image-option-button {
      width: $optionsButtonDim;
      height: $optionsButtonDim;
      @apply bg-gray-900 rounded my-0 mx-1 flex items-center content-center;
    }
  }
}

body.ProseMirror-resizing {
  cursor: col-resize !important;
}
</style>
